import React, { useState } from 'react';

const statusColors = {
  safe: { bg: 'bg-safe/10', text: 'text-safe', border: 'border-safe/20', dot: 'bg-safe' },
  warning: { bg: 'bg-caution/10', text: 'text-caution', border: 'border-caution/20', dot: 'bg-caution' },
  danger: { bg: 'bg-danger/10', text: 'text-danger', border: 'border-danger/20', dot: 'bg-danger' },
};

function DetailValue({ label, value }) {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'boolean') value = value ? 'Oui' : 'Non';
  if (typeof value === 'object') return null; // skip nested objects in flat view
  return (
    <div className="flex justify-between py-1 border-b border-dark-border/30 last:border-0">
      <span className="text-gray-500 text-xs">{label}</span>
      <span className="text-gray-300 text-xs font-mono text-right max-w-[60%] break-all">{String(value)}</span>
    </div>
  );
}

function DetailSection({ title, data }) {
  if (!data || (typeof data === 'object' && Object.keys(data).length === 0)) return null;
  if (Array.isArray(data)) {
    if (data.length === 0) return null;
    return (
      <div className="mt-2">
        <p className="text-gray-400 text-xs font-semibold mb-1">{title}</p>
        <div className="space-y-1 pl-2">
          {data.map((item, i) => (
            <div key={i} className="text-xs text-gray-400 bg-dark-surface/50 rounded px-2 py-1">
              {typeof item === 'object' ? (
                <div className="space-y-0.5">
                  {Object.entries(item).filter(([, v]) => v !== null && v !== '' && typeof v !== 'object').map(([k, v]) => (
                    <div key={k} className="flex justify-between gap-2">
                      <span className="text-gray-500">{k}:</span>
                      <span className="text-gray-300 font-mono text-right break-all">{String(typeof v === 'boolean' ? (v ? 'Oui' : 'Non') : v)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <span className="text-gray-300 font-mono">{String(item)}</span>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }
  return null;
}

export default function SourceCard({ source }) {
  const [expanded, setExpanded] = useState(false);
  const colors = statusColors[source.status] || statusColors.safe;
  const details = source.details || {};

  // Separate flat fields from arrays/objects for display
  const flatFields = [];
  const arrayFields = [];
  const skipKeys = new Set(['checked', 'error']);

  Object.entries(details).forEach(([key, val]) => {
    if (skipKeys.has(key)) return;
    if (Array.isArray(val)) {
      arrayFields.push([key, val]);
    } else if (val !== null && val !== undefined && val !== '') {
      if (typeof val === 'object') {
        // Nested object -> show as array of one
        arrayFields.push([key, [val]]);
      } else {
        flatFields.push([key, val]);
      }
    }
  });

  const hasDetails = flatFields.length > 0 || arrayFields.length > 0;

  return (
    <div className={`glass-card border-l-4 ${colors.border} transition-all`}>
      <button
        type="button"
        onClick={() => hasDetails && setExpanded(!expanded)}
        className={`w-full text-left p-4 ${hasDetails ? 'cursor-pointer' : 'cursor-default'}`}
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${colors.dot}`} />
            <h4 className="text-white font-medium text-sm">{source.name}</h4>
          </div>
          <div className="flex items-center gap-2">
            <span className={`${colors.bg} ${colors.text} px-2 py-0.5 rounded text-xs font-medium`}>
              {source.metric}
            </span>
            {hasDetails && (
              <svg
                className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
                fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
              </svg>
            )}
          </div>
        </div>
        <p className="text-gray-400 text-sm">{source.summary}</p>
      </button>

      {expanded && hasDetails && (
        <div className="px-4 pb-4 border-t border-dark-border/30 pt-3">
          {/* Flat key-value pairs */}
          {flatFields.length > 0 && (
            <div className="mb-2">
              {flatFields.map(([key, val]) => (
                <DetailValue key={key} label={key} value={val} />
              ))}
            </div>
          )}

          {/* Arrays / nested objects */}
          {arrayFields.map(([key, val]) => (
            <DetailSection key={key} title={key} data={val} />
          ))}
        </div>
      )}
    </div>
  );
}
